import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './RecoveryMap.css';
import recoveryData from '../data/recoveryResources.json';

// Fix for default marker icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const JAMAICA_CENTER = [18.1096, -77.2975];

const RecoveryMap = () => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [currentFilter, setCurrentFilter] = useState('red-cross');

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Initialize map
    const map = L.map(mapRef.current, {
      center: JAMAICA_CENTER,
      zoom: 9,
      maxZoom: 18,
      zoomControl: true
    });

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(map);

    mapInstanceRef.current = map;

    // Add Red Cross markers
    addRecoveryMarkers(map);

    // Force resize
    setTimeout(() => {
      map.invalidateSize();
    }, 100);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const addRecoveryMarkers = (map) => {
    const markers = [];

    // Add Red Cross locations
    recoveryData.redCrossLocations.forEach(location => {
      // Create custom icon for Red Cross
      const redCrossIcon = L.divIcon({
        className: 'custom-recovery-marker',
        html: `
          <div class="recovery-marker red-cross-marker">
            <div class="marker-icon">🏥</div>
          </div>
        `,
        iconSize: [40, 40],
        iconAnchor: [20, 40],
        popupAnchor: [0, -40]
      });

      const marker = L.marker([location.lat, location.lon], { icon: redCrossIcon });

      const popupContent = `
        <div class="recovery-popup">
          <div class="popup-header red-cross-header">
            <span class="popup-icon">🏥</span>
            <strong>${location.name}</strong>
          </div>
          <div class="popup-content">
            <p><strong>📍 Address:</strong><br/>${location.address}<br/>${location.parish}, Jamaica</p>
            <p><strong>📞 Phone:</strong><br/><a href="tel:${location.phone}">${location.phone}</a></p>
            <p><strong>✅ Status:</strong> <span class="status-operational">Operational</span></p>
            <div class="services-list">
              <strong>Services Available:</strong>
              <ul>
                ${location.services.map(service => `<li>✓ ${service}</li>`).join('')}
              </ul>
            </div>
            <a href="https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.lon}" 
               target="_blank" 
               class="directions-btn">
              🗺️ Get Directions
            </a>
          </div>
        </div>
      `;

      marker.bindPopup(popupContent, {
        maxWidth: 300,
        className: 'custom-recovery-popup'
      });

      // Add tooltip that shows on hover
      const tooltipContent = `
        <div class="recovery-tooltip">
          <strong>${location.name}</strong><br/>
          📍 ${location.parish}<br/>
          📞 ${location.phone}
        </div>
      `;

      marker.bindTooltip(tooltipContent, {
        permanent: false,
        direction: 'top',
        offset: [0, -35],
        className: 'custom-recovery-tooltip'
      });

      marker.recoveryData = location;
      markers.push(marker);
      marker.addTo(map);
    });

    markersRef.current = markers;
  };

  const handleFilterChange = (filter) => {
    setCurrentFilter(filter);

    if (!mapInstanceRef.current) return;

    markersRef.current.forEach(marker => {
      const category = marker.recoveryData?.category;

      if (filter === 'all') {
        marker.addTo(mapInstanceRef.current);
      } else if (filter === category) {
        marker.addTo(mapInstanceRef.current);
      } else {
        marker.remove();
      }
    });
  };

  return (
    <div id="recovery-map" className="recovery-map-wrapper">
      <div className="recovery-map-header">
        <div className="header-content">
          <h2>🏥 Recovery Resources Map</h2>
          <p className="subtitle">Find emergency services, supplies, and support across Jamaica</p>
        </div>
      </div>

      <div className="recovery-controls">
        <button 
          className={`recovery-btn red-cross-btn ${currentFilter === 'red-cross' ? 'active' : ''}`}
          onClick={() => handleFilterChange('red-cross')}
        >
          Red Cross
        </button>
        <button 
          className="recovery-btn disabled"
          disabled
          title="Coming Soon"
        >
          Meals
        </button>
        <button 
          className="recovery-btn disabled"
          disabled
          title="Coming Soon"
        >
          Water
        </button>
        <button 
          className="recovery-btn disabled"
          disabled
          title="Coming Soon"
        >
          WiFi
        </button>
      </div>

      <div ref={mapRef} className="recovery-map-container" />

      <div className="recovery-legend">
        <h4>📍 Available Resources</h4>
        <div className="legend-item active">
          <span className="legend-marker red-cross">🏥</span>
          <div>
            <strong>Jamaica Red Cross</strong>
            <p>Emergency relief, first aid, shelter, food</p>
          </div>
        </div>
        <div className="legend-item disabled">
          <span className="legend-marker">🍽️</span>
          <div>
            <strong>Meals on Wheels</strong>
            <p className="coming-soon-text">Coming Soon - Mobile food distribution</p>
          </div>
        </div>
        <div className="legend-item disabled">
          <span className="legend-marker">💧</span>
          <div>
            <strong>Fresh Water Stands</strong>
            <p className="coming-soon-text">Coming Soon - Clean water distribution</p>
          </div>
        </div>
        <div className="legend-item disabled">
          <span className="legend-marker">🏥</span>
          <div>
            <strong>Hospital Status</strong>
            <p className="coming-soon-text">Coming Soon - Hospital operations</p>
          </div>
        </div>
      </div>

      <div className="recovery-info-box">
        <h4>🚨 Emergency Contacts</h4>
        <ul>
          <li><strong>Emergency Services:</strong> <a href="tel:119">119</a></li>
          <li><strong>Jamaica Red Cross HQ:</strong> <a href="tel:876-984-7860">(876) 984-7860</a></li>
          <li><strong>ODPEM (Emergency Management):</strong> <a href="tel:876-906-9674">876-906-9674</a></li>
        </ul>
      </div>
    </div>
  );
};

export default RecoveryMap;
